//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { GetPinterestEngagementUserMetricsResponse } from '@bvengineering/pinterest-gateway-service-types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import * as pinterestAnalyticsApi from '@apis/pinterest-analytics/pinterestAnalyticsApi';
import { IMlsMaterializedPinWithMetrics } from '@apis/pinterest-analytics/pinterestAnalyticsApiModel';
import {
  getAliasedDateRange,
  IAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import {
  EngagementMetric,
  TopPinsTableView,
} from '@pinterest/pinterestConstants';
import { IPinterestEngagementPageState } from '@pinterest/redux/engagement/PinterestEngagementModel';
import { isDateEarlierThan30DaysAgo } from '@pinterest/redux/selectors/getShouldDisableLast30Days';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { RequestError } from '@utils/sessionFetch';

import { crl8 } from 'app/curalate.globals';

const initialState: IPinterestEngagementPageState = {
  dateRange: getAliasedDateRange(
    'last30Days',
    crl8.session?.timeZone.id ?? TimeZoneId.Utc,
  ),
  summaryStats: undefined,
  dailyStats: [],
  topPins: {
    shouldReturnOnlyLast30Days: false,
    sortKey: EngagementMetric.Impressions,
    view: TopPinsTableView.TABLE,
    offset: 0,
    limit: 8,
    pins: [],
  },
  flags: {
    isFetchingEngagementMetrics: false,
    isFetchingTopPins: false,
  },
  errors: {
    fetchEngagementMetricsError: undefined,
    fetchTopPinsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.PinterestEngagement,
  initialState,
  reducers: {
    requestEngagementMetrics(state) {
      state.flags.isFetchingEngagementMetrics = true;
      state.errors.fetchEngagementMetricsError = undefined;
    },
    receiveEngagementMetricsSucceeded(
      state,
      action: PayloadAction<GetPinterestEngagementUserMetricsResponse>,
    ) {
      state.flags.isFetchingEngagementMetrics = false;
      state.summaryStats = action.payload.data?.summaryMetrics
        ? { ...action.payload.data.summaryMetrics }
        : state.summaryStats;
      state.dailyStats = action.payload.data?.dailyMetrics
        ? [...action.payload.data.dailyMetrics]
        : state.dailyStats;
    },
    receiveEngagementMetricsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingEngagementMetrics = false;
      state.errors.fetchEngagementMetricsError = action.payload;
    },
    requestTopPins(state) {
      state.flags.isFetchingTopPins = true;
      state.errors.fetchTopPinsError = undefined;
    },
    receiveTopPinsSucceeded(
      state,
      action: PayloadAction<IMlsMaterializedPinWithMetrics[]>,
    ) {
      state.topPins.pins = action.payload;
      state.flags.isFetchingTopPins = false;
    },
    receiveTopPinsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingTopPins = false;
      state.errors.fetchTopPinsError = action.payload;
    },
    updateDateRange(state, action: PayloadAction<IAliasedDateRange>) {
      state.dateRange = action.payload;
    },
    updateTopPinsDisplayState(
      state,
      action: PayloadAction<{
        shouldReturnOnlyLast30Days?: boolean;
        sortKey?: EngagementMetric;
        view?: TopPinsTableView;
        offset?: number;
        limit?: number;
      }>,
    ) {
      state.topPins.shouldReturnOnlyLast30Days =
        action.payload.shouldReturnOnlyLast30Days ??
        state.topPins.shouldReturnOnlyLast30Days;
      state.topPins.sortKey = action.payload.sortKey ?? state.topPins.sortKey;
      state.topPins.view = action.payload.view ?? state.topPins.view;
      state.topPins.offset = action.payload.offset ?? state.topPins.offset;
      state.topPins.limit = action.payload.limit ?? state.topPins.limit;
    },
  },
});

export const changeDateRangeAndRefreshContent = createThunk(
  (dateRange: IAliasedDateRange) => (dispatch, getState) => {
    dispatch(actions.updateDateRange(dateRange));

    // If the new date range falls outside the last 30 days, disable `shouldReturnOnlyLast30Days`
    const userTimezone = getState().session.timeZone.id;
    const shouldDisableLast30Days = isDateEarlierThan30DaysAgo(
      dateRange.endDate,
      userTimezone,
    );

    if (shouldDisableLast30Days) {
      dispatch(
        updateTopPinsDisplayState({
          shouldReturnOnlyLast30Days: false,
        }),
      );
    }

    void dispatch(fetchTopPins());
    void dispatch(fetchEngagementMetrics());
  },
);

export const fetchEngagementMetrics = createThunk(
  () => async (dispatch, getState) => {
    const { dateRange } = getState().pinterest.engagement;

    dispatch(actions.requestEngagementMetrics());

    try {
      const engagementMetricsData =
        await pinterestAnalyticsApi.getEngagementMetrics({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        });

      dispatch(
        actions.receiveEngagementMetricsSucceeded(engagementMetricsData),
      );
    } catch (e) {
      const error = e as RequestError;

      dispatch(actions.receiveEngagementMetricsFailed(error.message));
    }
  },
);

export const fetchTopPins = createThunk(() => async (dispatch, getState) => {
  dispatch(actions.requestTopPins());

  const {
    dateRange,
    topPins: { shouldReturnOnlyLast30Days, sortKey },
  } = getState().pinterest.engagement;

  try {
    const res = await pinterestAnalyticsApi.getTopPins({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortByMetric: sortKey,
      createdInLast30DaysFilter: shouldReturnOnlyLast30Days,
    });
    dispatch(actions.receiveTopPinsSucceeded(res));
  } catch (e) {
    const error = e as RequestError;
    dispatch(actions.receiveTopPinsFailed(error.message));
  }
});

export const updateTopPinsFiltersAndFetchContent = createThunk(
  (filterState: {
    shouldReturnOnlyLast30Days?: boolean;
    sortKey?: EngagementMetric;
  }) =>
    async (dispatch, getState) => {
      dispatch(
        updateTopPinsDisplayState({
          ...filterState,
          offset: 0, // Since we are fetching new content, return to the first page of results
        }),
      );

      await dispatch(fetchTopPins());
    },
);

export const { updateTopPinsDisplayState } = actions;

export default reducer;
